import { useEffect, useRef, useState } from 'react';
import { signIn, signOut } from 'aws-amplify/auth';
import { Controller, useForm } from 'react-hook-form';
import { LoaderCircle, MapPin, Package2 } from 'lucide-react';
import { CURRENT_ENV, SCREEN_TYPES } from 'utils/constants';
import { getStations } from 'services/api/auth';
import { useAppStore, useAuthStore } from 'store';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getSessionUser } from '../utils';
import { ChangePassword } from './ChangePassword';
import { ForgotPassword } from './ForgotPassword';

type LoginFormValues = {
  email: string;
  password: string;
  stationId: string;
};

type Station = {
  station_id: string;
  station_name: string;
};

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'login' | 'changePassword' | 'selectStation' | 'forgotPassword'>(
    'login'
  );
  const [stations, setStations] = useState<Station[]>([]);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const { stationId, setStationId, setAuthenticatedUser } = useAuthStore();
  const { setCurrentScreen } = useAppStore();

  const { register, handleSubmit, watch, setValue, control } = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
      password: '',
      stationId: stationId || '',
    },
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');
  const selectedStationId = watch('stationId');
  const { ref: emailFieldRef, ...emailFieldProps } = register('email');
  const { ref: passwordFieldRef, ...passwordFieldProps } = register('password');

  useEffect(() => {
    if (step !== 'login' || loading) {
      return;
    }

    const focusTimer = window.setTimeout(() => emailInputRef.current?.focus(), 0);

    return () => window.clearTimeout(focusTimer);
  }, [step, loading]);

  const handleEmailEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    passwordInputRef.current?.focus();
  };

  const completeLogin = (selectedStation: string) => {
    setStationId(selectedStation);
    setCurrentScreen(SCREEN_TYPES.APP_SELECT);
    toast.success('Login successful');
  };

  const handleLogin = async ({ email, password }: LoginFormValues): Promise<void> => {
    if (!email || !password) {
      toast.warning('Please enter email and password');
      return;
    }

    setLoading(true);

    try {
      let signInResult;
      try {
        signInResult = await signIn({ username: email, password });
      } catch (error) {
        const err = error as { message?: string; name?: string };

        if (
          err.name === 'UserAlreadyAuthenticatedException' ||
          err.message?.toLowerCase().includes('already')
        ) {
          await signOut();
          signInResult = await signIn({ username: email, password });
        } else {
          throw error;
        }
      }

      if (signInResult?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setStep('changePassword');
        return;
      }

      const sessionUser = await getSessionUser();
      setAuthenticatedUser(sessionUser);

      const stationsResponse = await getStations();
      const nextStations: Station[] = Array.isArray(stationsResponse?.stations)
        ? stationsResponse.stations
        : [];

      if (!nextStations.length) {
        throw new Error('No stations available for this user');
      }

      setStations(nextStations);

      if (nextStations.length === 1) {
        const onlyStationId = nextStations[0].station_id;
        completeLogin(onlyStationId);
        return;
      }

      if (stationId && nextStations.some((station) => station.station_id === stationId)) {
        setValue('stationId', stationId);
        completeLogin(stationId);
        return;
      }

      setStep('selectStation');
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStation = async ({ stationId }: LoginFormValues): Promise<void> => {
    if (!stationId) {
      toast.warning('Please select a station');
      return;
    }

    setLoading(true);

    try {
      completeLogin(stationId);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async () => {
    setLoading(true);

    try {
      try {
        await signOut();
      } catch {
        // ignore signOut failure here
      }

      setStep('login');
      setStations([]);
      setAuthenticatedUser(null);
      setValue('stationId', '');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 py-6">
      <div className="mx-auto w-full max-w-[390px]">
        <Card className="overflow-hidden border-slate-200/80 bg-white/95 backdrop-blur">
          <CardHeader className="space-y-5 bg-gradient-to-b from-slate-50 via-white to-white pb-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Package2 className="h-7 w-7" />
            </div>

            <CardTitle className="text-left text-2xl font-semibold text-slate-900">
              VSR Management System
            </CardTitle>

            <p className="text-left text-sm leading-6 text-slate-500">Receive &amp; Grade Items</p>

            <span className="inline-block rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium ">
              Current Environment: <b className="text-primary">{CURRENT_ENV}</b>
            </span>
          </CardHeader>

          <CardContent className="space-y-6">
            {step === 'changePassword' ? (
              <ChangePassword
                onSuccess={() => {
                  setStep('login');
                  setValue('password', '');
                }}
                onBack={handleBack}
              />
            ) : step === 'forgotPassword' ? (
              <ForgotPassword onBack={() => setStep('login')} />
            ) : step === 'login' ? (
              <form className="space-y-5" onSubmit={handleSubmit(handleLogin)}>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    disabled={loading}
                    onKeyDown={handleEmailEnter}
                    {...emailFieldProps}
                    ref={(element) => {
                      emailFieldRef(element);
                      emailInputRef.current = element;
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    isPassword
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    disabled={loading}
                    {...passwordFieldProps}
                    ref={(element) => {
                      passwordFieldRef(element);
                      passwordInputRef.current = element;
                    }}
                  />
                </div>

                <div className="text-right">
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={() => setStep('forgotPassword')}
                  >
                    Forgot password?
                  </button>
                </div>

                <Button
                  type="submit"
                  className="h-12 w-full rounded-2xl text-base font-semibold"
                  disabled={!emailValue || !passwordValue || loading}
                >
                  {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit(handleSelectStation)}>
                <div className="space-y-2">
                  <Label htmlFor="stationId">Select Station</Label>

                  <Controller
                    name="stationId"
                    control={control}
                    render={({ field }) => (
                      <div className="relative">
                        <div className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-primary">
                          <MapPin className="h-4 w-4" />
                        </div>

                        <Select
                          disabled={loading}
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            setStationId(value);
                          }}
                        >
                          <SelectTrigger id="stationId" className="pl-11">
                            <SelectValue placeholder="Select a station" />
                          </SelectTrigger>

                          <SelectContent>
                            {stations.map((station) => (
                              <SelectItem key={station.station_id} value={station.station_id}>
                                {station.station_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  />
                </div>

                <div className="space-y-3">
                  <Button
                    type="submit"
                    className="h-12 w-full rounded-2xl text-base font-semibold"
                    disabled={!selectedStationId || loading}
                  >
                    {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                    {loading ? 'Continuing...' : 'Continue'}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-full rounded-2xl text-base"
                    disabled={loading}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
